import {OrderStore} from "../order/OrderStore";
import {FeatureNames} from "../../../types/UserConfig";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {Observable, ReplaySubject} from "rxjs";
import {OrderViewModel, OrderViewModels} from "../../models/OrderViewModel";
import {IressOrderViewModelFactory} from "./IressOrderViewModelFactory";
import {OrderQuoteSourceBuilder} from "./OrderQuoteSourceBuilder";
import {OrderDestination, OrderRecord, OrderRecords} from "../../models/OrderRecord";
import {AccountStore} from "../../../account/store/AccountStore";
import {AccountBriefRecord} from "../../../account/types/AccountBriefRecord";
import {
    isIressOrder,
    isIressTmcOrder
} from "../../models/iress/IressOrderRecord";
import {isSimulatedOrder} from "../../models/simulated/SimulatedOrderRecord";
import {SimulatedOrderViewModelFactory} from "./SimulatedOrderViewModelFactory";
import {PlacedOrdersStore} from "../placed/PlacedOrdersStore";
import {isBrokerOrder, isSimulatedOrder as isSimulatedPlacedOrder, PlacedOrders} from "../placed/models/PlacedOrder";
import {quoteHashMap, QuoteHashMap} from "../../../quote/util/QuoteStoreUtil";
import {QuoteViewModel} from "../../../quote/models/QuoteViewModel";
import {BehaviorSubject} from "rxjs/BehaviorSubject";

export class OrderViewStore {

    public static readonly id = "orderViewStore";

    private readonly models$ = new ReplaySubject<ReadonlyArray<OrderViewModel>>(1);
    private readonly currentPortfolioModels$: Observable<OrderViewModels>;

    /** @ngInject */
    public constructor(private readonly orderStore: OrderStore,
                       private readonly currentUserService: CurrentUserService,
                       private readonly iressOrderViewModelFactory: IressOrderViewModelFactory,
                       private readonly simulatedOrderViewModelFactory: SimulatedOrderViewModelFactory,
                       private readonly orderQuoteSourceBuilder: OrderQuoteSourceBuilder,
                       private readonly accountStore: AccountStore,
                       private readonly placedOrdersStore: PlacedOrdersStore) {
        if (currentUserService.hasFeature(FeatureNames.SCREEN_EQUITY_ORDERS, FeatureNames.SCREEN_OPTION_ORDERS)) {
            this.init();
            this.currentPortfolioModels$ = this.buildCurrentPortfolioModels();
        } else {
            this.currentPortfolioModels$ = new BehaviorSubject([]);
        }
    }

    private init() {
        const orders$ = this.orderStore.orders;
        const quoteSource$ = this.quoteSource();
        const accountSource$ = this.accountSource();

        Observable.combineLatest(orders$, quoteSource$, accountSource$, this.placedOrdersStore.placed)
                  .auditTime(1000)
                  .switchMap(([orders, quotes, accounts, placed]) => this.buildModels(orders, quotes, accounts, placed))
                  .subscribe((models) => this.models$.next(models));
    }

    private quoteSource(): Observable<QuoteHashMap<QuoteViewModel>> {
        return this.orderQuoteSourceBuilder
                   .build()
                   .map((quotes) => {
                       const quoteMap = quoteHashMap<QuoteViewModel>();
                       quotes.forEach((q) => quoteMap.set(q, q));
                       return quoteMap;
                   });
    }

    private accountSource(): Observable<Map<string, AccountBriefRecord>> {
        return this.accountStore
                   .accounts
                   .map((accounts) => {
                       const accountsMap = new Map<string, AccountBriefRecord>();
                       accounts.forEach((a) => accountsMap.set(a.number, a));
                       return accountsMap;
                   });
    }

    private buildModels(orders: OrderRecords,
                        quotes: QuoteHashMap<QuoteViewModel>,
                        accounts: Map<string, AccountBriefRecord>,
                        placed: PlacedOrders): Observable<OrderViewModels> {
        const finalOrders: Array<OrderRecord> = [];
        orders.forEach(o => {
            finalOrders.push(o);
            if (!(isIressTmcOrder(o) || isIressOrder(o))) {
                return;
            }
            if (o.destination === OrderDestination.DESK && o.childOrders && o.childOrders.length > 0) {
                o.childOrders.forEach(child => finalOrders.push({...child, parentOrderLimit: o.price}));
            }
        });
        const iressOrders = finalOrders.filter(isIressOrder);
        const placedIressOrders = placed.filter(isBrokerOrder);
        const simulatedOrders = finalOrders.filter(isSimulatedOrder);
        const placedSimulatedOrders = placed.filter(isSimulatedPlacedOrder);

        const iressModels$ = this.iressOrderViewModelFactory.build(iressOrders, quotes, accounts, placedIressOrders);
        const simulatedModels$ = this.simulatedOrderViewModelFactory.build(simulatedOrders, quotes, accounts, placedSimulatedOrders);
        return Observable.forkJoin(iressModels$, simulatedModels$)
                         .map(([iress, simulated]) => [...iress, ...simulated]);
    }

    private buildCurrentPortfolioModels(): Observable<OrderViewModels> {
        return Observable.combineLatest(this.models$, this.accountStore.current)
                         .map(([models, portfolio]) => this.portfolioModels(models, portfolio))
                         .startWith([])
                         .publishReplay(1)
                         .refCount();
    }

    private portfolioModels(models: OrderViewModels, account: AccountBriefRecord | null): OrderViewModels {
        return models.filter((m) => m.account?.number === account?.number);
    }

    public get orders(): Observable<OrderViewModels> {
        return this.models$.map((m) => [...m]);
    }

    public get currentPortfolioOrders(): Observable<OrderViewModels> {
        return this.currentPortfolioModels$;
    }
}
